/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/auth/AuthProvider.tsx
 * Release: v2.0.0 - Provider autenticazione completo Coronatek V2
 * Data: Dicembre 2024
 */

import React, { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

interface User {
  id: string;
  email: string;
  name: string | null;
  surname: string | null;
  role: string;
  group_id: string;
  is_verified: boolean;
  status?: string;
  has_profile?: boolean;
  needs_profile_completion?: boolean;
  auth_type?: 'email' | 'google' | string;
  pending_email?: string | null;
  email_change_requested_at?: string | null;
  has_pending_email_change?: boolean;
  pending_email_grace_period_expired?: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (email: string, password: string, name?: string, surname?: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  getToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const API_BASE = import.meta.env.DEV ? 'http://localhost:3002/api' : '/api';

  // Verifica token all'avvio
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        // Fix: gestisci anche stringa "null"
        const hasValidToken = token && token !== 'null' && token !== 'undefined';
        if (hasValidToken) {
          await refreshUser();
        }
      } catch (error) {
        console.error('Errore verifica autenticazione:', error);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Refresh dati utente
  const refreshUser = async (): Promise<any> => {
    try {
      const token = localStorage.getItem('accessToken');
      // Fix: gestisci anche stringa "null" (alcuni browser/polyfill salvano null come stringa)
      const hasValidToken = token && token !== 'null' && token !== 'undefined';
      console.log('🔍 [DEBUG v2.1] refreshUser - token RAW:', token);
      console.log('🔍 [DEBUG v2.1] refreshUser - hasValidToken:', hasValidToken);
      if (!hasValidToken) {
        console.log('✅ [DEBUG v2.1] No valid token found, skipping refresh');
        return;
      }

      const response = await fetch(`${API_BASE}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('🔍 [DEBUG] /auth/me response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('🔍 [DEBUG] /auth/me data received:', data);
        setUser(data.user);
        console.log('✅ [DEBUG] User set successfully:', data.user);
        return data.user; // Ritorna i dati utente
      } else {
        console.log('❌ [DEBUG] /auth/me failed, trying refresh...');
        // Token scaduto, prova refresh
        await refreshToken();
        return null;
      }
    } catch (error) {
      console.error('❌ [DEBUG] Errore refresh utente:', error);
      logout();
      return null;
    }
  };

  // Refresh token (compatibile vecchia/nuova release)
  const refreshToken = async () => {
    try {
      const storedRefresh = localStorage.getItem('refreshToken');
      if (!storedRefresh) throw new Error('Refresh token non trovato');

      const resp = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ refreshToken: storedRefresh })
      });

      const text = await resp.text();
      if (!resp.ok) throw new Error(text || 'Refresh token non valido');

      let data: any;
      try { data = JSON.parse(text); } catch { throw new Error('Refresh response non valida'); }

      const newToken = data?.token ?? data?.access_token;
      const newRefresh = data?.refreshToken ?? data?.refresh_token;
      if (!newToken || !newRefresh) throw new Error('Refresh response non valida');

      localStorage.setItem('accessToken', newToken);
      localStorage.setItem('refreshToken', newRefresh);
      await refreshUser();
    } catch (error) {
      console.error('Errore refresh token:', error);
      logout();
    }
  };

  // Login (compatibile vecchia/nuova release e risposte non-JSON)
  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const resp = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const text = await resp.text();
      if (!resp.ok) {
        let errorMessage = 'Errore durante il login';
        try { const err = JSON.parse(text); if (err?.error) errorMessage = err.error; } catch {}
        return { success: false, error: errorMessage };
      }

      let data: any = null;
      try { data = JSON.parse(text); } catch { return { success: false, error: 'Risposta server non valida' }; }

      const token = data?.token ?? data?.access_token ?? data?.data?.token;
      const refresh = data?.refreshToken ?? data?.refresh_token ?? data?.data?.refreshToken;
      const user = data?.user ?? data?.profile ?? data?.data?.user ?? null;

      console.log('🔍 [DEBUG] Login parsing - token:', !!token, 'refresh:', !!refresh, 'user:', !!user);
      console.log('🔍 [DEBUG] User data:', user);

      if (token && refresh && user) {
        localStorage.setItem('accessToken', token);
        localStorage.setItem('refreshToken', refresh);
        console.log('🔍 [DEBUG] Tokens saved to localStorage');
        setUser(user);
        console.log('✅ [DEBUG] User set in state:', user);
        return { success: true };
      }
      console.log('❌ [DEBUG] Login failed - missing fields in response:', { token: !!token, refresh: !!refresh, user: !!user });
      return { success: false, error: 'Risposta server non valida' };
    } catch (error) {
      console.error('Errore login:', error);
      return { success: false, error: 'Errore di connessione' };
    }
  };

  // Registrazione
  const register = async (email: string, password: string, name?: string, surname?: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password, name, surname })
      });

      const data = await response.json();

      if (response.ok) {
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Errore durante la registrazione' };
      }
    } catch (error) {
      console.error('Errore registrazione:', error);
      return { success: false, error: 'Errore di connessione' };
    }
  };

  // Logout
  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('userId');
    setUser(null);
    // Redirect a login se non siamo già lì e non siamo su pagine mobile
    const path = window.location.pathname;
    if (!path.startsWith('/login') && !path.startsWith('/mobile-operator') && !path.startsWith('/mobile-officina')) {
      window.location.href = '/login';
    }
  };

  // Get token
  const getToken = () => {
    return localStorage.getItem('accessToken');
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    refreshUser,
    getToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve essere usato all\'interno di AuthProvider');
  }
  return context;
};